import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { NoteForm } from "@/components/sales-notes/NoteForm";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { useCompany } from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";

const searchSchema = z.object({
  fromInvoice: z.string().optional(),
});

export const Route = createFileRoute("/companies/$companyId/notas-debito/new")({
  component: NewDebitNotePage,
  validateSearch: searchSchema,
});

function NewDebitNotePage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewDebitNoteContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewDebitNoteContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { fromInvoice } = Route.useSearch();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const companyQuery = useCompany(companyId);

  if (companyQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (companyQuery.isError || !companyQuery.data) {
    return (
      <ErrorState
        title={t("notes.list.error_title")}
        description={t("notes.list.error_body")}
        onRetry={() => void companyQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nota_debito" />
      <div>
        <Link
          to="/companies/$companyId/notas-debito"
          params={{ companyId }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("notes.new.back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t("notes.new.title.debit")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("notes.new.subtitle.debit")}
        </p>
      </div>
      <NoteForm
        noteType="debit"
        companyId={companyId}
        company={companyQuery.data}
        fromInvoiceId={fromInvoice}
      />
    </div>
  );
}
